import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { MyTicketsPanel } from "@/components/tasks/MyTicketsPanel";
import { TeamBoard } from "@/components/tasks/TeamBoard";
import { CreateTicketDialog } from "@/components/tickets/CreateTicketDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ListChecks, AlertTriangle, CalendarClock, CalendarDays, Circle, Briefcase, ChevronRight, Plus, LifeBuoy, Users, Pencil, Trash2, UserRound, Repeat, AlarmClock, TreePalm } from "lucide-react";
import { toast } from "sonner";
import { REPEAT_LABEL } from "@/components/tasks/TaskDialog";
import { RecurringActions } from "@/components/tasks/RecurringActions";
import { OutOfOfficeDialog, fmtRange } from "@/components/tasks/OutOfOfficeDialog";
import { CoverageDialog } from "@/components/tasks/CoverageDialog";

const localMidnight = () => new Date(new Date().toDateString());
const fmtDue = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return iso; } };
const SECTIONS = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, cls: "text-destructive" },
  { key: "soon", label: "Due this week", icon: CalendarClock, cls: "text-amber-600" },
  { key: "later", label: "Later", icon: CalendarDays, cls: "text-foreground" },
  { key: "none", label: "No due date", icon: Circle, cls: "text-muted-foreground" },
];
const bucketOf = (due) => {
  if (!due) return "none";
  const d = new Date(due + "T00:00:00"), today = localMidnight(), wk = new Date(today); wk.setDate(wk.getDate() + 7);
  return d < today ? "overdue" : d <= wk ? "soon" : "later";
};

const Tile = ({ label, value, cls, testid }) => (
  <div className="border border-border bg-card rounded-sm p-4" data-testid={testid}>
    <div className={`text-3xl font-condensed font-extrabold ${cls}`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
  </div>
);

function WorkRow({ w, overdue, onComplete, onEdit, onDelete, onChanged, canDelete, showAssignee }) {
  const navigate = useNavigate();
  const isTask = w.kind === "task";
  return (
    <div data-testid={isTask ? "mytask-task" : "mytask-item"} className="flex items-center gap-3 border border-border bg-card rounded-sm px-3 py-2.5 hover:border-primary/60 transition-colors">
      <Checkbox data-testid="mytask-complete" onCheckedChange={() => onComplete(w)} className="shrink-0" />
      <button onClick={() => (isTask ? onEdit(w) : navigate("/matter"))} className="flex-1 min-w-0 text-left">
        <div className="text-sm font-medium truncate">{w.title}</div>
        <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
          {isTask ? <ListChecks className="h-3 w-3 shrink-0" /> : <Briefcase className="h-3 w-3 shrink-0" />}
          {isTask ? (w.matter_label ? `Task · ${w.matter_label}` : "Task") : `${w.matter_number || w.client_name} · ${w.client_name}`}
          {isTask && w.repeat && <span className="inline-flex items-center gap-1 ml-1 text-primary" title={REPEAT_LABEL[w.repeat]} data-testid="mytask-repeat"><Repeat className="h-3 w-3" />{REPEAT_LABEL[w.repeat]}</span>}
          {isTask && w.snoozed_from && <span className="inline-flex items-center gap-1 ml-1 text-amber-600" title={`Originally due ${fmtDue(w.snoozed_from)}`} data-testid="mytask-snoozed"><AlarmClock className="h-3 w-3" />Snoozed</span>}
          {showAssignee && isTask && <span className="inline-flex items-center gap-1 ml-1"><UserRound className="h-3 w-3" />{w.assignee_name}</span>}
          {isTask && w.created_by_email !== w.assignee_email && !showAssignee && <span className="ml-1">· from {w.created_by_name}</span>}
        </div>
      </button>
      {w.due && <span className={`text-xs whitespace-nowrap ${overdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>{fmtDue(w.due)}</span>}
      {isTask ? (
        <span className="flex items-center gap-2 shrink-0">
          {w.repeat && <RecurringActions task={w} onChanged={onChanged} />}
          <button onClick={() => onEdit(w)} className="text-muted-foreground hover:text-primary" data-testid="mytask-edit"><Pencil className="h-3.5 w-3.5" /></button>
          {canDelete && <button onClick={() => onDelete(w)} className="text-muted-foreground hover:text-destructive" data-testid="mytask-delete"><Trash2 className="h-3.5 w-3.5" /></button>}
        </span>
      ) : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
    </div>
  );
}

export default function MyTasks() {
  const { user } = useAuth();
  const isMgr = ["admin", "manager"].includes(user?.role);
  const [matterTasks, setMatterTasks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [delegated, setDelegated] = useState([]);
  const [work, setWork] = useState({ assigned: [], requested: [] });
  const [groups, setGroups] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const [boardKey, setBoardKey] = useState(0);
  const [oooOpen, setOooOpen] = useState(false);
  const [myOoo, setMyOoo] = useState([]);
  const location = useLocation();
  const coverageParam = new URLSearchParams(location.search).get("coverage");
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageWeek, setCoverageWeek] = useState("next");
  useEffect(() => { if (coverageParam && isMgr) { setCoverageWeek(coverageParam === "this" ? "this" : "next"); setCoverageOpen(true); } }, [coverageParam, isMgr]);
  const openCoverage = () => { setCoverageWeek("next"); setCoverageOpen(true); };

  const load = useCallback(() => Promise.all([
    api.get("/matters/my-tasks").then((r) => setMatterTasks(r.data.tasks || [])),
    api.get("/tasks", { params: { scope: "mine" } }).then((r) => setTasks(r.data)),
    api.get("/tasks", { params: { scope: "delegated" } }).then((r) => setDelegated(r.data)),
    api.get("/tickets/my-work").then((r) => setWork(r.data)),
    api.get("/ooo", { params: { scope: "mine" } }).then((r) => setMyOoo(r.data)),
  ]).catch((e) => console.error("my tasks load", e)), []);
  useEffect(() => { load(); api.get("/support-groups").then((r) => setGroups(r.data)).catch((e) => console.error("groups", e)); }, [load]);

  const items = [
    ...matterTasks.map((t) => ({ ...t, kind: "matter", title: t.item, key: `m-${t.matter_id}-${t.stage}-${t.item}` })),
    ...tasks.map((t) => ({ ...t, kind: "task", key: `t-${t.id}` })),
  ];
  const grouped = {};
  items.forEach((w) => { const b = bucketOf(w.due); (grouped[b] = grouped[b] || []).push(w); });
  Object.values(grouped).forEach((l) => l.sort((a, b) => (a.due || "9999").localeCompare(b.due || "9999")));
  const todayIso = localMidnight().toISOString().slice(0, 10);
  const overdue = items.filter((w) => w.due && w.due < todayIso).length;

  const complete = async (w) => {
    try {
      if (w.kind === "task") {
        const { data } = await api.patch(`/tasks/${w.id}`, { done: true });
        toast.success(data.next_task ? `Task completed — next one scheduled for ${fmtDue(data.next_task.due)}` : "Task completed");
      } else {
        await api.patch(`/matters/${w.matter_id}/checklist`, { stage: w.stage, item: w.item, done: true });
        toast.success("Task completed");
      }
      load();
    } catch (e) { console.error("complete", e); toast.error("Could not update task"); }
  };
  const removeTask = async (w) => {
    try { await api.delete(`/tasks/${w.id}`); toast.success("Task deleted"); load(); }
    catch (e) { console.error("delete task", e); toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const edit = (w) => { setEditing(w); setPreset(null); setTaskOpen(true); };
  const addFor = (p) => { setEditing(null); setPreset({ assignee_email: p.email, assignee_name: p.name }); setTaskOpen(true); };
  const afterSave = () => { load(); setBoardKey((k) => k + 1); };

  const myWork = (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-6">
        {items.length === 0 ? (
          <EmptyState icon={ListChecks} title="Nothing on your plate" hint="Create a task with New Task, or wait for a manager to assign you a matter." />
        ) : SECTIONS.map((s) => (grouped[s.key]?.length ? (
          <div key={s.key} data-testid={`mytasks-section-${s.key}`}>
            <div className={`flex items-center gap-2 mb-2 text-sm font-semibold ${s.cls}`}><s.icon className="h-4 w-4" /> {s.label} <span className="text-muted-foreground font-normal">({grouped[s.key].length})</span></div>
            <div className="space-y-2">
              {grouped[s.key].map((w) => <WorkRow key={w.key} w={w} overdue={s.key === "overdue"} onComplete={complete} onEdit={edit} onDelete={removeTask} onChanged={afterSave} canDelete={isMgr || w.created_by_email === user?.email} />)}
            </div>
          </div>
        ) : null))}

        {delegated.length > 0 && (
          <div data-testid="mytasks-delegated">
            <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Users className="h-4 w-4 text-primary" /> Delegated by me <span className="text-muted-foreground font-normal">({delegated.length})</span></div>
            <div className="space-y-2">
              {delegated.map((t) => <WorkRow key={t.id} w={{ ...t, kind: "task" }} overdue={t.due && t.due < todayIso} onComplete={complete} onEdit={edit} onDelete={removeTask} onChanged={afterSave} canDelete showAssignee />)}
            </div>
          </div>
        )}
      </div>
      <MyTicketsPanel work={work} />
    </div>
  );

  return (
    <div data-testid="my-tasks-page">
      <PageHeader overline="Operations" title="My Tasks" subtitle="Your matter checklist items, personal and delegated tasks, and Monge Ticketing requests — all in one place.">
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" data-testid="ooo-btn" onClick={() => setOooOpen(true)}><TreePalm className="h-4 w-4 mr-1.5" /> Out of Office</Button>
          <Button variant="outline" data-testid="new-ticket-from-tasks" onClick={() => setTicketOpen(true)}><LifeBuoy className="h-4 w-4 mr-1.5" /> New Ticket</Button>
          <Button data-testid="new-task-btn" onClick={() => { setEditing(null); setPreset(null); setTaskOpen(true); }}><Plus className="h-4 w-4 mr-1.5" /> New Task</Button>
        </div>
      </PageHeader>

      {myOoo[0] && (
        <button onClick={() => setOooOpen(true)} data-testid="ooo-banner" className="w-full text-left flex items-center gap-2.5 mb-4 px-3 py-2 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 text-sm hover:border-amber-400 transition-colors">
          <TreePalm className="h-4 w-4 shrink-0" />
          <span className="font-medium">{myOoo[0].status === "away" ? "You’re out of office" : "You’re scheduled out"}</span>
          <span>{fmtRange(myOoo[0].start, myOoo[0].end)} · back {fmtDue(myOoo[0].back)}</span>
          <span className="text-amber-700/80 hidden sm:inline">· recurring tasks due while you’re away are parked on your first day back</span>
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Tile label="Overdue" value={overdue} cls="text-destructive" testid="mytasks-overdue" />
        <Tile label="Due this week" value={(grouped.soon || []).length} cls="text-amber-600" testid="mytasks-soon" />
        <Tile label="Open tasks" value={items.length} cls="text-foreground" testid="mytasks-total" />
        <Tile label="Open tickets" value={work.assigned.length + work.requested.length} cls="text-primary" testid="mytasks-tickets" />
      </div>

      {isMgr ? (
        <Tabs defaultValue="mine">
          <TabsList className="mb-5">
            <TabsTrigger value="mine" data-testid="tab-my-work"><ListChecks className="h-4 w-4 mr-1.5" /> My work</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team-board"><Users className="h-4 w-4 mr-1.5" /> Team board</TabsTrigger>
          </TabsList>
          <TabsContent value="mine">{myWork}</TabsContent>
          <TabsContent value="team"><TeamBoard onAddFor={addFor} onCoverage={openCoverage} refreshKey={boardKey} /></TabsContent>
        </Tabs>
      ) : myWork}

      <TaskDialog open={taskOpen} setOpen={setTaskOpen} task={editing} preset={preset} onSaved={afterSave} />
      <OutOfOfficeDialog open={oooOpen} setOpen={setOooOpen} onChanged={afterSave} />
      {isMgr && <CoverageDialog open={coverageOpen} setOpen={setCoverageOpen} initialWeek={coverageWeek} />}
      <CreateTicketDialog groups={groups} open={ticketOpen} setOpen={setTicketOpen} onCreated={load} trigger={<span className="hidden" />} />
    </div>
  );
}
